// src/pages/admin/AdminPayouts.jsx - COMPLETE UPDATED
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TablePagination,
  TextField,
  Divider,
  Snackbar,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import {
  AttachMoney,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
  Visibility,
  Download,
  Check,
  Close,
  MoreVert,
  AccountBalanceWallet,
  Send,
} from '@mui/icons-material';

import {
  formatCurrency,
  formatDate,
  getStatusColor,
  getStatusIcon,
  getPayoutStatusInfo,
  getPayoutMethod,
  getPayoutMethodName,
  PAYOUT_METHODS,
  calculatePayoutStats,
} from '../../services/financeService';

const AdminPayouts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    pendingCount: 0,
    totalPendingAmount: 0,
    completedCount: 0,
    totalCompletedAmount: 0,
    processingCount: 0,
    totalProcessingAmount: 0,
    failedCount: 0,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [transferData, setTransferData] = useState({
    bankName: '',
    accountNumber: '',
    accountName: '',
    amount: '',
    reference: '',
    notes: '',
    payoutMethod: 'bank_transfer',
    phoneNumber: '',
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchPayouts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getPayouts({
        page: page + 1,
        limit: rowsPerPage,
      });
      
      const data = response.data || response || {};
      const payoutsList = data.payouts || [];
      setPayouts(payoutsList);
      setTotalCount(data.total || 0);
      
      // Calculate stats using the shared function
      const calculatedStats = calculatePayoutStats(payoutsList);
      setStats(calculatedStats);
    } catch (error) {
      setError('Failed to load payout requests. Please try again.');
      console.error('Error fetching payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
  }, [page, rowsPerPage]);

  const handleMenuOpen = (event, payout) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayout(payout);
    if (payout?.Vendor) {
      const method = payout.payoutMethod || 'bank_transfer';
      
      setTransferData({
        bankName: payout.Vendor.bankName || '',
        accountNumber: payout.Vendor.bankAccountNumber || '',
        accountName: payout.Vendor.bankAccountName || payout.Vendor.storeName || '',
        amount: payout.amount?.toString() || '',
        reference: `PAYOUT-${payout.id}-${Date.now()}`,
        notes: `Payout to ${payout.Vendor.storeName || 'Vendor'}`,
        payoutMethod: method,
        phoneNumber: payout.payoutPhoneNumber || payout.Vendor.payoutPhoneNumber || '',
      });
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    setOpenDetailsDialog(true);
    handleMenuClose();
  };

  const handleOpenTransfer = () => {
    setOpenTransferDialog(true);
    handleMenuClose();
  };

  const handleTransferChange = (field) => (event) => {
    setTransferData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handlePayoutMethodChange = (event) => {
    const methodId = event.target.value;
    setTransferData((prev) => ({
      ...prev,
      payoutMethod: methodId,
    }));
  };

  const getSelectedMethod = () => {
    return getPayoutMethod(transferData.payoutMethod);
  };

  const handleProcessTransfer = async () => {
    if (!selectedPayout) return;
    
    const selectedMethod = getSelectedMethod();
    
    if (selectedMethod?.requiresBank && (!transferData.bankName || !transferData.accountNumber || !transferData.accountName)) {
      showSnackbar('Please provide complete bank details for bank transfer', 'error');
      return;
    }
    
    if (selectedMethod?.requiresPhone && !transferData.phoneNumber) {
      showSnackbar('Please provide phone number for mobile money payout', 'error');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const payload = {
        bankName: transferData.bankName,
        accountNumber: transferData.accountNumber,
        accountName: transferData.accountName,
        amount: parseFloat(transferData.amount),
        reference: transferData.reference,
        notes: transferData.notes,
        payoutMethod: transferData.payoutMethod,
        phoneNumber: transferData.phoneNumber,
      };

      const response = await adminApi.processPayoutTransfer(selectedPayout.id, payload);

      if (response.success) {
        showSnackbar(`Payout of ${formatCurrency(selectedPayout.amount)} transferred successfully via ${selectedMethod.name}!`, 'success');
        setOpenTransferDialog(false);
        setOpenApproveDialog(false);
        fetchPayouts();
      } else {
        showSnackbar(response.message || 'Transfer failed', 'error');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to process transfer.', 'error');
    } finally {
      setIsProcessing(false);
      setSelectedPayout(null);
    }
  };

  const handleApprovePayout = async () => {
    if (!selectedPayout) return;
    setIsProcessing(true);

    try {
      await adminApi.updatePayoutStatus(selectedPayout.id, 'processing');
      showSnackbar(`Payout request approved and marked for processing`, 'success');
      setOpenApproveDialog(false);
      fetchPayouts();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to approve payout.', 'error');
    } finally {
      setIsProcessing(false);
      setSelectedPayout(null);
    }
  };

  const handleRejectPayout = async () => {
    if (!selectedPayout) return;
    setIsProcessing(true);

    try {
      await adminApi.updatePayoutStatus(selectedPayout.id, 'failed');
      showSnackbar(`Payout request rejected.`, 'success');
      setOpenRejectDialog(false);
      fetchPayouts();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to reject payout.', 'error');
    } finally {
      setIsProcessing(false);
      setSelectedPayout(null);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && payouts.length === 0 && page === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedMethod = getSelectedMethod();

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
              Vendor Payouts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review, approve, and process vendor withdrawal requests
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchPayouts} disabled={loading} size={isMobile ? 'small' : 'medium'}>
              Refresh
            </Button>
            <Button variant="outlined" startIcon={<Download />} disabled={payouts.length === 0} size={isMobile ? 'small' : 'medium'}>
              Export
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Pending color="warning" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {stats.pendingCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">Pending Requests</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {formatCurrency(stats.totalPendingAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Pending color="info" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="info.main">
                {stats.processingCount || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Processing</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {formatCurrency(stats.totalProcessingAmount || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <CheckCircle color="success" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {stats.completedCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">Completed</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {formatCurrency(stats.totalCompletedAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <AccountBalanceWallet color="primary" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {stats.totalCompletedAmount > 0 ? formatCurrency(stats.totalCompletedAmount) : '0.00'}
              </Typography>
              <Typography variant="caption" color="text.secondary">Total Paid Out</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payouts Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell align="right">Available Balance</TableCell>
                <TableCell align="right">Requested Amount</TableCell>
                <TableCell>Payout Method</TableCell>
                <TableCell>Requested At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No payout requests found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payouts.map((payout) => {
                  const methodInfo = getPayoutMethod(payout.payoutMethod);
                  
                  return (
                    <TableRow key={payout.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">#{payout.id}</Typography>
                        {payout.transactionRef && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Ref: {payout.transactionRef}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: 12 }}>
                            {payout.Vendor?.storeName?.[0]?.toUpperCase() || 'V'}
                          </Avatar>
                          <Typography variant="body2">{payout.Vendor?.storeName || 'Unknown Vendor'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {formatCurrency(payout.availableBalance || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                          {formatCurrency(payout.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <span>{methodInfo?.icon || '🏦'}</span>
                          <Typography variant="body2">{methodInfo?.name || 'Bank Transfer'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(payout.requestedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(payout.status)}
                          label={payout.status || 'Pending'}
                          size="small"
                          color={getStatusColor(payout.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, payout)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewDetails}>
          <Visibility fontSize="small" sx={{ mr: 1 }} /> View Details
        </MenuItem>
        {selectedPayout?.status === 'pending' && (
          <>
            <MenuItem onClick={() => { setOpenApproveDialog(true); handleMenuClose(); }} sx={{ color: 'success.main' }}>
              <Check fontSize="small" sx={{ mr: 1 }} /> Approve Payout
            </MenuItem>
            <MenuItem onClick={handleOpenTransfer} sx={{ color: 'primary.main' }}>
              <Send fontSize="small" sx={{ mr: 1 }} /> Transfer Money
            </MenuItem>
            <MenuItem onClick={() => { setOpenRejectDialog(true); handleMenuClose(); }} sx={{ color: 'error.main' }}>
              <Close fontSize="small" sx={{ mr: 1 }} /> Reject Payout
            </MenuItem>
          </>
        )}
      </Menu>

      {/* View Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoney color="primary" />
            <Typography variant="h6">Payout Request Details</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayout && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Vendor</Typography>
                <Typography variant="body1" fontWeight="600">
                  {selectedPayout.Vendor?.storeName || 'Unknown'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Available Balance</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {formatCurrency(selectedPayout.availableBalance || 0)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Amount Requested</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {formatCurrency(selectedPayout.amount)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Payout Method</Typography>
                <Chip
                  label={getPayoutMethodName(selectedPayout.payoutMethod)}
                  color="primary"
                  variant="outlined"
                />
                {selectedPayout.payoutPhoneNumber && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Phone: {selectedPayout.payoutPhoneNumber}
                  </Typography>
                )}
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">Bank Details</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Bank Name</Typography>
                      <Typography variant="body2" fontWeight="500">
                        {selectedPayout.Vendor?.bankName || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Account Number</Typography>
                      <Typography variant="body2" fontWeight="500" sx={{ fontFamily: 'monospace' }}>
                        {selectedPayout.Vendor?.bankAccountNumber || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Account Name</Typography>
                      <Typography variant="body2" fontWeight="500">
                        {selectedPayout.Vendor?.bankAccountName || selectedPayout.Vendor?.storeName || 'N/A'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Chip
                  icon={getStatusIcon(selectedPayout.status)}
                  label={selectedPayout.status || 'Pending'}
                  color={getStatusColor(selectedPayout.status)}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Request Date</Typography>
                <Typography variant="body2">{formatDate(selectedPayout.requestedAt)}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Money Dialog */}
      <Dialog open={openTransferDialog} onClose={() => setOpenTransferDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Send color="primary" />
            <Typography variant="h6">Transfer Money to Vendor</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayout && (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    {selectedPayout.Vendor?.storeName?.[0]?.toUpperCase() || 'V'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {selectedPayout.Vendor?.storeName || 'Unknown Vendor'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Request #{selectedPayout.id} • {formatCurrency(selectedPayout.amount)}
                    </Typography>
                    <Typography variant="caption" color="success.main">
                      Available Balance: {formatCurrency(selectedPayout.availableBalance || 0)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Payout Method Selection */}
              <FormControl fullWidth>
                <InputLabel>Payout Method</InputLabel>
                <Select
                  value={transferData.payoutMethod}
                  onChange={handlePayoutMethodChange}
                  label="Payout Method"
                >
                  {PAYOUT_METHODS.map((method) => (
                    <MenuItem key={method.id} value={method.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>{method.icon}</span>
                        <span>{method.name}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select the payout method for this vendor</FormHelperText>
              </FormControl>

              {/* Conditional Fields based on method */}
              {selectedMethod?.requiresBank && (
                <>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary.main" sx={{ mt: 1 }}>
                    Bank Transfer Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Bank Name *"
                        value={transferData.bankName}
                        onChange={handleTransferChange('bankName')}
                        placeholder="e.g., Commercial Bank of Ethiopia"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Number *"
                        value={transferData.accountNumber}
                        onChange={handleTransferChange('accountNumber')}
                        placeholder="e.g., 1000123456789"
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Account Name *"
                        value={transferData.accountName}
                        onChange={handleTransferChange('accountName')}
                        placeholder="e.g., John Doe"
                        required
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {selectedMethod?.requiresPhone && (
                <TextField
                  fullWidth
                  label="Phone Number *"
                  value={transferData.phoneNumber}
                  onChange={handleTransferChange('phoneNumber')}
                  placeholder="e.g., 0912345678"
                  required
                  helperText="Required for mobile money payouts"
                />
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Amount"
                    value={transferData.amount}
                    onChange={handleTransferChange('amount')}
                    disabled
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reference"
                    value={transferData.reference}
                    onChange={handleTransferChange('reference')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={transferData.notes}
                    onChange={handleTransferChange('notes')}
                    placeholder="Additional notes about this transfer"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>

              <Alert severity="info">
                <Typography variant="body2">
                  This transfer will be processed using <strong>{selectedMethod?.name}</strong>.
                  The vendor will receive a notification once the transfer is complete.
                </Typography>
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setOpenTransferDialog(false)} fullWidth={isMobile}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleProcessTransfer}
            disabled={isProcessing || (selectedMethod?.requiresBank && (!transferData.bankName || !transferData.accountNumber)) || (selectedMethod?.requiresPhone && !transferData.phoneNumber)}
            fullWidth={isMobile}
            startIcon={<Send />}
          >
            {isProcessing ? <CircularProgress size={20} /> : 'Send Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={openApproveDialog} onClose={() => setOpenApproveDialog(false)} maxWidth="xs">
        <DialogTitle sx={{ color: 'success.main' }}>
          <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} /> Approve Payout
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve the payout of{' '}
            <strong>{formatCurrency(selectedPayout?.amount || 0)}</strong> to{' '}
            <strong>{selectedPayout?.Vendor?.storeName || 'Vendor'}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            This will mark the payout for processing. You can then transfer the money.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)} disabled={isProcessing}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprovePayout} disabled={isProcessing}>
            {isProcessing ? <CircularProgress size={20} /> : 'Approve Payout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="xs">
        <DialogTitle sx={{ color: 'error.main' }}>
          <Cancel sx={{ mr: 1, verticalAlign: 'middle' }} /> Reject Payout
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reject the payout request from{' '}
            <strong>{selectedPayout?.Vendor?.storeName || 'Vendor'}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            The request will be marked as failed and the vendor will be notified.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)} disabled={isProcessing}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRejectPayout} disabled={isProcessing}>
            {isProcessing ? <CircularProgress size={20} /> : 'Reject Payout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPayouts;